/**
 * Derived-total computation for catalog fields marked with
 * `field.computed`.
 *
 * Three pieces:
 *   - `computeFieldSum` — pure: evaluates `sum` against caller-supplied
 *     read functions.
 *   - `useComputedTotalSync` — React effect: subscribes to engine
 *     revisions, recomputes, writes the result back to the engine if
 *     it changed. Idempotent — never loops because it short-circuits
 *     on equal values.
 *   - `ComputedTotalTile` — read-only display surface. Replaces the
 *     plain NumberInput a `computed` field would otherwise render as,
 *     so users see "We're adding this up for you" instead of being
 *     asked to sum line items they already typed.
 *
 * Why renderer-owned (not engine-owned): the catalog declares the
 * relationship; the renderer is the only place that already
 * subscribes to engine revisions per visible section. Adding a
 * compute layer to the form-engine would entangle it with React
 * re-render semantics for a feature exactly two surfaces consume
 * (intake UI + read-only review panel). The PDF engine reads the
 * stored value normally — the renderer's job is just to keep it
 * accurate.
 */
import { useEffect, useMemo } from 'react';
import type { FieldDefinition, FieldId } from '@inside-us-aos/domain';
import type { FormEngine } from '@inside-us-aos/form-engine';

export interface IRepeatableInstanceValue {
  instanceId: string;
  value: number;
}

/**
 * Read a singleton field's numeric value from `(fieldId) => value`.
 * Returns `undefined` when absent or not a number; callers in tests
 * pass a lambda backed by a Map, the renderer passes a closure over
 * `engine.getAnswer`.
 */
export type TReadSingletonNumber = (fieldId: FieldId) => number | undefined;

/**
 * Read every instance of a repeatable field as `{instanceId, value}`.
 * Used to sum across all rows of a repeatable group (e.g.,
 * household-income contributors).
 */
export type TReadRepeatableInstances = (
  groupId: string,
) => readonly IRepeatableInstanceValue[];

/**
 * Pure: evaluate `field.computed` against the supplied readers.
 * Returns 0 when:
 *   - the field has no `computed` spec
 *   - all addends are absent / non-numeric
 *   - any individual addend reads as undefined or NaN (treated as 0)
 *
 * Never throws and never returns NaN — callers can safely persist the
 * result without poisoning downstream consumers.
 */
export function computeFieldSum(
  field: FieldDefinition,
  readSingleton: TReadSingletonNumber,
  readRepeatable: TReadRepeatableInstances,
): number {
  const spec = field.computed;
  if (spec === undefined || spec.op !== 'sum') return 0;

  let total = 0;
  for (const addend of spec.addends) {
    if (typeof addend === 'string') {
      const v = readSingleton(addend);
      if (typeof v === 'number' && Number.isFinite(v)) total += v;
      continue;
    }
    const instances = readRepeatable(addend.groupId);
    for (const { value } of instances) {
      if (typeof value === 'number' && Number.isFinite(value)) total += value;
    }
  }
  return total;
}

/**
 * Read the current numeric answer for a singleton field from the
 * form engine. Returns `undefined` when no answer exists or the
 * stored value isn't a number.
 */
function readEngineNumberSingleton(
  engine: FormEngine,
  fieldId: FieldId,
): number | undefined {
  const ans = engine.getAnswer(fieldId);
  if (ans?.type === 'number') return ans.value;
  return undefined;
}

/**
 * Read every instance of a repeatable field from the engine. Uses
 * `getRepeatableState(groupId).instances` to enumerate instance ids,
 * then reads each instance's value at `repeatableField`. Returns an
 * empty list if the group has no instances.
 */
function readEngineRepeatable(
  engine: FormEngine,
  groupId: string,
  repeatableField: FieldId,
): readonly IRepeatableInstanceValue[] {
  const state = engine.getRepeatableState(groupId);
  if (state === null) return [];
  const out: IRepeatableInstanceValue[] = [];
  for (const inst of state.instances) {
    if (inst.instanceId === undefined) continue;
    const ans = engine.getAnswer(repeatableField, inst.instanceId);
    if (ans?.type === 'number' && Number.isFinite(ans.value)) {
      out.push({ instanceId: inst.instanceId, value: ans.value });
    }
  }
  return out;
}

/**
 * Hook: keep a computed field's stored value in sync with its
 * addends. Runs on every engine revision, recomputes the sum, and
 * writes back via `setAnswer` only when the new sum differs from
 * the stored value. Idempotent — does NOT loop because the
 * equality check short-circuits the second pass.
 *
 * Pass the visible computed fields the section is rendering; the
 * hook subscribes once and iterates them all on each revision.
 */
export function useComputedTotalSync(
  engine: FormEngine,
  computedFields: readonly FieldDefinition[],
): void {
  // Stable reference so the effect doesn't tear down on every render.
  const fields = useMemo(() => computedFields, [computedFields]);

  useEffect(() => {
    const sync = (): void => {
      for (const field of fields) {
        if (field.computed === undefined) continue;
        const next = computeFieldSum(
          field,
          id => readEngineNumberSingleton(engine, id),
          groupId => {
            // Find which repeatable addend in the spec uses this
            // groupId so we read the right field per instance.
            const ref = field.computed?.addends.find(
              a => typeof a !== 'string' && a.groupId === groupId,
            );
            if (ref === undefined || typeof ref === 'string') return [];
            return readEngineRepeatable(engine, groupId, ref.repeatableField);
          },
        );
        const current = readEngineNumberSingleton(engine, field.id);
        if (current === next) continue;
        engine.setAnswer(field.id, { type: 'number', value: next });
      }
    };
    // Run once up-front in case the engine already has addends from
    // hydrate — without this the computed field would stay at its
    // pre-hydrate stale value until the user typed.
    sync();
    return engine.subscribe(sync);
  }, [engine, fields]);
}
